import React from "react";

import {
  FaClipboardList,
} from "react-icons/fa";

import {
  TOURNAMENT_WORKFLOW_STEPS,
} from "../data/tournamentAboutData";

import useInViewOnce from "../hooks/useInViewOnce";

function WorkflowStep({
  step,
  index,
}) {
  const Icon = step.icon;

  return (
    <article className="group relative overflow-hidden rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm transition duration-300 hover:-translate-y-1 hover:border-orange-200 hover:shadow-xl hover:shadow-orange-500/10 dark:border-white/10 dark:bg-slate-900 dark:hover:border-orange-400/30">
      <div className="pointer-events-none absolute -right-8 -top-8 h-28 w-28 rounded-full bg-orange-400/10 blur-2xl" />

      <div className="relative flex items-start gap-4">
        <div className="relative shrink-0">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-orange-500 to-amber-500 text-lg font-bold text-white shadow-lg shadow-orange-500/20">
            {index + 1}
          </div>

          <div className="absolute -bottom-2 -right-2 flex h-8 w-8 items-center justify-center rounded-xl border-2 border-white bg-blue-600 text-white shadow-md dark:border-slate-900">
            <Icon className="h-3.5 w-3.5" />
          </div>
        </div>

        <div className="min-w-0">
          <h3 className="text-lg font-bold text-slate-950 dark:text-white">
            {step.title}
          </h3>

          <p className="mt-2 text-sm leading-7 text-slate-600 dark:text-slate-300">
            {step.description}
          </p>
        </div>
      </div>
    </article>
  );
}

export default function TournamentWorkflowSection() {
  const {
    ref,
    isVisible,
  } = useInViewOnce();

  return (
    <section
      ref={ref}
      className={`relative mt-16 overflow-hidden rounded-[38px] border border-slate-200 bg-slate-50 p-5 shadow-xl shadow-slate-950/5 transition-all duration-700 dark:border-white/10 dark:bg-white/[0.03] sm:mt-20 sm:p-8 lg:p-10 ${
        isVisible
          ? "translate-y-0 opacity-100"
          : "translate-y-8 opacity-0"
      }`}
    >
      <div className="pointer-events-none absolute -left-24 top-8 h-80 w-80 rounded-full bg-blue-300/20 blur-3xl dark:bg-blue-500/10" />

      <div className="pointer-events-none absolute -right-24 bottom-8 h-80 w-80 rounded-full bg-orange-300/20 blur-3xl dark:bg-orange-500/10" />

      <div className="relative">
        <div className="max-w-4xl">
          <div className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-blue-700 dark:border-blue-400/20 dark:bg-blue-500/10 dark:text-blue-300">
            <FaClipboardList />
            Déroulé d’un tournoi
          </div>

          <h2 className="mt-5 text-3xl font-bold tracking-tight text-slate-950 sm:text-4xl lg:text-5xl dark:text-white">
            Un parcours clair, de la création de la compétition jusqu’aux podiums
          </h2>

          <p className="mt-5 max-w-3xl text-base leading-8 text-slate-600 sm:text-lg dark:text-slate-300">
            Chaque étape s’enchaîne dans la même application. Les informations saisies au départ alimentent automatiquement les pesées, les répartitions, les combats et les résultats finaux.
          </p>
        </div>

        <div className="mt-10 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {TOURNAMENT_WORKFLOW_STEPS.map(
            (step, index) => (
              <WorkflowStep
                key={step.title}
                step={step}
                index={index}
              />
            ),
          )}
        </div>

        <div className="mt-8 overflow-hidden rounded-[30px] border border-orange-200 bg-orange-50 p-5 dark:border-orange-400/20 dark:bg-orange-500/10 sm:p-6">
          <div className="grid items-center gap-6 lg:grid-cols-[1fr_380px]">
            <div>
              <h3 className="text-xl font-bold text-slate-950 sm:text-2xl dark:text-white">
                Les informations circulent automatiquement entre les étapes
              </h3>

              <p className="mt-3 text-sm leading-7 text-slate-700 dark:text-slate-300">
                Une modification réalisée lors de la pesée est prise en compte dans la répartition. Un résultat saisi par une table met à jour la poule, le classement et l’affichage public.
              </p>
            </div>

            <div className="overflow-hidden rounded-[24px] border border-white bg-white p-2 shadow-lg dark:border-white/10 dark:bg-slate-950/50">
              <img
                src="/images/Tournoi04.jpg"
                alt="Suivi des combats pendant un tournoi de judo"
                className="aspect-video w-full rounded-[18px] object-cover"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}